import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { ConteudoCreateInput, type ConteudoCreateInputType } from '@shared/dto';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { useApiMutation, useApiQuery } from '../../../hooks/use-api';
import { materiasApi } from '../../../api/materias.api';

export function ConteudosDaMateria({ materiaId }: { materiaId: string }) {
  const { data: conteudos, loading, refetch } = useApiQuery(materiasApi.listarConteudos, materiaId);
  const { mutate: remover } = useApiMutation(materiasApi.removerConteudo);

  async function aoConfirmarRemocao(conteudoId: string) {
    await remover(conteudoId);
    void refetch();
  }

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-md" />
          ))}
        </div>
      ) : null}

      {!loading && conteudos?.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum conteúdo cadastrado ainda.</p>
      ) : null}

      <ul className="space-y-2">
        {conteudos?.map((conteudo) => (
          <li
            key={conteudo.id}
            className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
          >
            <span>{conteudo.nome}</span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Remover conteúdo">
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remover &quot;{conteudo.nome}&quot;?</AlertDialogTitle>
                  <AlertDialogDescription>
                    O conteúdo deixa de aparecer em qualquer lista. Essa ação não pode ser desfeita
                    por aqui.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => void aoConfirmarRemocao(conteudo.id)}>
                    Remover
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </li>
        ))}
      </ul>

      <NovoConteudoForm materiaId={materiaId} onCriado={refetch} />
    </div>
  );
}

function NovoConteudoForm({ materiaId, onCriado }: { materiaId: string; onCriado: () => void }) {
  const { mutate, loading } = useApiMutation(materiasApi.criarConteudo);
  const form = useForm<ConteudoCreateInputType>({
    resolver: zodResolver(ConteudoCreateInput),
    defaultValues: { materiaId, nome: '' },
  });

  async function onSubmit(dados: ConteudoCreateInputType) {
    await mutate(dados);
    form.reset({ materiaId, nome: '' });
    onCriado();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex items-start gap-2">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input placeholder="Novo conteúdo..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" variant="outline" size="sm" disabled={loading}>
          <Plus className="size-4" />
          Adicionar
        </Button>
      </form>
    </Form>
  );
}
